import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function AboutPage() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setStatus('success');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // Response is not JSON
      }

      const serverCode = (parsed as { code?: string }).code;
      const serverMsg = (parsed as { meta?: { message?: string } }).meta?.message
        || (parsed as { message?: string }).message
        || responseText;

      if (response.ok && serverCode === 'OK') {
        setStatus('success');
        setFormState({ name: '', email: '', message: '' });
      } else {
        setStatus('error');
        setErrorMsg(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查您的网络连接后重试');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩国漫画网',
    url: 'https://rhmeiji.cn',
    description: '韩国漫画网提供免费的韩漫在线观看和下拉式韩漫大全阅读，提供便捷访问方式，让您随时随地沉浸于多样漫画世界无需成本。',
    email: 'contact@rhmeiji.cn',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '首尔特别市',
      addressRegion: '江南区',
      addressCountry: 'KR',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@rhmeiji.cn',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
      hoursAvailable: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00',
        },
      ],
    },
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩国漫画网',
    description: '了解韩国漫画网的品牌故事、平台优势及联系方式。韩国漫画网致力于为全球华人读者提供优质的韩漫在线阅读服务。',
    url: 'https://rhmeiji.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国漫画网',
      url: 'https://rhmeiji.cn',
    },
  };

  const stats = [
    { icon: 'ri-book-open-line', value: '500+', label: '漫画作品' },
    { icon: 'ri-user-heart-line', value: '50万+', label: '活跃读者' },
    { icon: 'ri-global-line', value: '100+', label: '覆盖国家和地区' },
    { icon: 'ri-thumb-up-line', value: '99.2%', label: '用户好评率' },
  ];

  const features = [
    {
      icon: 'ri-smartphone-line',
      title: '全设备适配',
      desc: '支持电脑、平板、手机等多种设备，随时随地畅享韩漫阅读。',
    },
    {
      icon: 'ri-download-cloud-line',
      title: '下拉式阅读',
      desc: '创新的下拉式漫画阅读体验，流畅自然，沉浸感十足。',
    },
    {
      icon: 'ri-money-cny-circle-line',
      title: '完全免费',
      desc: '所有漫画内容免费开放，无需付费即可享受优质阅读体验。',
    },
    {
      icon: 'ri-speed-up-line',
      title: '每日更新',
      desc: '热门连载作品同步更新，精彩内容不间断。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '安全可靠',
      desc: '注重用户隐私保护，提供安全稳定的阅读环境。',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '优质服务',
      desc: '专业的客服团队，及时响应用户反馈和需求。',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <title>关于我们 - 韩国漫画网</title>
      <meta name="description" content="了解韩国漫画网的品牌故事、平台优势及联系方式。韩国漫画网致力于为全球华人读者提供优质的韩漫在线阅读服务，每日更新海量韩国漫画，完全免费。韩国漫画网, 韩漫在线观看, 免费韩漫阅读。" />
      <meta name="keywords" content="韩国漫画网, 关于我们, 韩漫在线观看, 免费韩漫阅读, 联系我们" />
      <link rel="canonical" href="https://rhmeiji.cn/about" />
      <meta property="og:title" content="关于我们 - 韩国漫画网" />
      <meta property="og:description" content="了解韩国漫画网的品牌故事、平台优势及联系方式。致力于为全球华人读者提供优质的韩漫在线阅读服务。" />
      <meta property="og:url" content="https://rhmeiji.cn/about" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <section className="mb-14">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                关于我们
              </h1>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-10">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-5 bg-background-50 rounded-xl border border-background-200/70"
                  >
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-xl">
                      <i className={`${stat.icon} text-lg text-accent-600`}></i>
                    </div>
                    <p className="text-xl md:text-2xl font-bold text-foreground-900">{stat.value}</p>
                    <p className="text-[11px] text-foreground-500 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8 mb-10">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">品牌故事</h2>
                <div className="text-sm text-foreground-700 leading-relaxed space-y-4">
                  <p>
                    韩国漫画网成立于2020年，是专注于韩漫在线阅读的中文平台。我们致力于为全球华人读者提供最优质的韩国漫画阅读体验，让每一位漫画爱好者都能轻松、便捷地享受韩漫的魅力。
                  </p>
                  <p>
                    平台汇集了恋爱、奇幻、动作、悬疑、搞笑等多种类型的韩国漫画，每日更新热门连载作品。凭借创新的下拉式阅读模式和全设备适配技术，韩国漫画网已成为众多韩漫爱好者的首选阅读平台。
                  </p>
                  <p>
                    我们坚持"免费、优质、便捷"的服务理念，所有漫画内容面向用户完全免费开放，让阅读没有门槛。同时，我们与韩国多家漫画工作室和平台保持合作，确保内容的正版性和更新速度。
                  </p>
                  <p>
                    未来，韩国漫画网将继续扩大漫画内容库，优化阅读体验，并探索更多创新功能，为读者打造更加沉浸式的韩漫阅读世界。感谢每一位读者的支持与陪伴！
                  </p>
                </div>
              </div>

              <div className="mb-10">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-6">平台优势</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {features.map((feature) => (
                    <div
                      key={feature.title}
                      className="p-5 bg-background-50 rounded-xl border border-background-200/70"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-xl mb-3">
                        <i className={`${feature.icon} text-lg text-primary-600`}></i>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{feature.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{feature.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-6">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-xl flex-shrink-0">
                      <i className="ri-mail-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-xs text-foreground-500">contact@rhmeiji.cn</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-xl flex-shrink-0">
                      <i className="ri-customer-service-2-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">在线客服</p>
                      <p className="text-xs text-foreground-500">工作日 9:00 - 18:00（韩国时间）</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-xl flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">公司地址</p>
                      <p className="text-xs text-foreground-500">韩国首尔特别市江南区</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-4">发送消息</h3>

                  {status === 'success' ? (
                    <div className="flex items-center gap-2 text-sm text-primary-600 bg-primary-50 rounded-lg px-4 py-3">
                      <i className="ri-checkbox-circle-line"></i>
                      消息已发送！我们会尽快回复您
                    </div>
                  ) : (
                    <form
                      onSubmit={handleSubmit}
                      action="https://readdy.ai/api/form/d9pbp8aftmtnfcfh0k3g"
                      method="POST"
                      className="space-y-3"
                      data-readdy-form="true"
                    >
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <div>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formState.name}
                          onChange={handleInputChange}
                          placeholder="您的姓名"
                          className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formState.email}
                          onChange={handleInputChange}
                          placeholder="您的邮箱"
                          className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          required
                          value={formState.message}
                          onChange={handleInputChange}
                          placeholder="请输入您的留言（最多500字）"
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        ></textarea>
                        <p className="text-[10px] text-foreground-400 mt-1 text-right">{formState.message.length}/500</p>
                      </div>
                      <button
                        type="submit"
                        disabled={status === 'loading'}
                        className="w-full py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                      >
                        {status === 'loading' ? '发送中...' : '发送消息'}
                      </button>
                    </form>
                  )}

                  {status === 'error' && (
                    <p className="mt-3 text-xs text-red-500">{errorMsg}</p>
                  )}
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}